import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiArrowRight, FiTruck, FiShield, FiClock, FiGlobe, FiPackage } from 'react-icons/fi';
import TrackShipment from '../components/TrackShipment';
import './Home.css';

const Home = () => {
  const features = [
    {
      icon: FiTruck,
      title: 'Fast Delivery',
      description: 'Express shipping services to get your packages delivered quickly and safely.',
    },
    {
      icon: FiShield,
      title: 'Secure Handling',
      description: 'Your packages are handled with the utmost care and security throughout the journey.',
    },
    {
      icon: FiClock,
      title: '24/7 Support',
      description: 'Round-the-clock customer support to assist you whenever you need help.',
    },
    {
      icon: FiGlobe,
      title: 'Global Reach',
      description: 'Worldwide shipping network connecting you to destinations across the globe.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <div className="home page-transition">
      <Helmet>
        <title>Home - Couriex</title>
        <meta name="description" content="Professional courier and logistics services in UAE. Fast, reliable, and secure shipping solutions." />
      </Helmet>
      {/* Track Shipment Section - Compact Professional Design */}
      <section className="track-section">
        <div className="track-section-wrapper">
          <div className="container">
            <motion.div
              className="track-section-container"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <TrackShipment showHeader={false} compact={true} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <motion.div
            className="hero-content"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.h1 className="hero-title" variants={itemVariants}>
              Your Trusted Logistics Partner
              <span className="hero-title-highlight"> Across UAE & Beyond</span>
            </motion.h1>
            <motion.p className="hero-description" variants={itemVariants}>
              Fast, reliable, and secure courier services connecting businesses and individuals 
              throughout the United Arab Emirates and worldwide. Experience seamless logistics 
              solutions tailored to your needs.
            </motion.p>
            <motion.div className="hero-buttons" variants={itemVariants}>
              <Link to="/services" className="btn btn-primary">
                Our Services
                <FiArrowRight />
              </Link>
              <Link to="/contact" className="btn btn-secondary">
                Get Quote
              </Link>
            </motion.div>
          </motion.div>
          <motion.div
            className="hero-image"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="hero-image-placeholder">
              <FiTruck size={200} />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.div
            className="features-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Why Choose Couriex</h2>
            <p className="section-description">
              Experience the difference with our comprehensive logistics solutions
            </p>
          </motion.div>

          <motion.div
            className="features-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                variants={itemVariants}
                whileHover={{ 
                  y: -15, 
                  scale: 1.05,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div 
                  className="feature-icon"
                  whileHover={{ 
                    rotate: [0, -10, 10, -10, 0],
                    scale: 1.1,
                    transition: { duration: 0.5 }
                  }}
                >
                  <feature.icon size={40} />
                </motion.div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="cta-title">Ready to <span>Ship</span>?</h2>
            <p className="cta-description">
              Get started today and experience professional courier services
            </p>
            <Link to="/contact" className="btn btn-primary btn-large">
              Contact Us Now
              <FiArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;

